import { Col, Row } from 'reactstrap';
import React, { useCallback } from 'react';
import SearchInput from 'components/SearchInput';
import { useApiGet, useApiPost } from 'hooks/useApi';
import Pagination from 'models/Pagination';
import PaginationNav from 'components/Pagination';
import { Link, useNavigate } from 'react-router-dom';
import Icon from 'components/Icon';
import Table from 'components/Table';
import { Column } from 'components/Table/_presenters/props/tableComponentProps';
import { AdminExternalNewsCategory } from 'models/AdminExternalNewsCategory';
import Button from 'components/Button';
import { useToast } from 'hooks/useToast';
import ToastType from 'enum/ToastType';
import moment from 'moment';

const AdminNewsCategoriesListPage = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [filteredData, setFilteredData] = React.useState({
    pageSize: 10,
    page: 1,
    search: ''
  });

  const { data: categoriesPage, isLoading, refetch } = useApiGet<
    Pagination<AdminExternalNewsCategory>
  >('/admin/news/categories', {
    params: filteredData
  });

  const { mutate: triggerAll, isPending: isTriggeringAll } = useApiPost<{
    message: string;
  }>('/admin/news/categories/update-content');

  const { mutate: triggerOne, isPending: isTriggeringOne } = useApiPost<{
    message: string;
  }>('/admin/news/categories');

  const handleSearch = useCallback(
    (search: string) => {
      setFilteredData({ ...filteredData, search, page: 1 });
    },
    [filteredData]
  );

  const handleClearFilter = useCallback(() => {
    setFilteredData({ pageSize: 10, page: 1, search: '' });
  }, []);

  const handlePageChange = useCallback(
    (page: number) => {
      setFilteredData({ ...filteredData, page });
    },
    [filteredData]
  );

  const goToEdit = useCallback(
    (category: AdminExternalNewsCategory) => {
      navigate(`/admin/news/categories/${category.id}`);
    },
    [navigate]
  );

  const handleTriggerAll = useCallback(() => {
    triggerAll(null as any, {
      onError: (response: any) => {
        addToast(
          ToastType.ERROR,
          response.errors?.detail?.toString() || 'Erro ao iniciar atualização'
        );
      },
      onSuccess: async () => {
        addToast(ToastType.SUCCESS, 'Atualização iniciada');
        await refetch();
      }
    });
  }, [addToast, refetch, triggerAll]);

  const handleTriggerOne = useCallback(
    (e: React.MouseEvent, categoryId: string) => {
      e.stopPropagation();
      triggerOne({ id: `${categoryId}/update-content` } as any, {
        onError: (response: any) => {
          addToast(
            ToastType.ERROR,
            response.errors?.detail?.toString() || 'Erro ao iniciar atualização'
          );
        },
        onSuccess: async () => {
          addToast(ToastType.SUCCESS, 'Atualização iniciada');
          await refetch();
        }
      });
    },
    [addToast, refetch, triggerOne]
  );

  const columns: Column<AdminExternalNewsCategory>[] = [
    {
      accessor: 'name',
      label: 'Nome',
      render: (name) => <span className="fw-medium">{String(name)}</span>
    },
    {
      accessor: 'channel' as keyof AdminExternalNewsCategory,
      label: 'Canal',
      render: (_: any, category: AdminExternalNewsCategory) => (
        <span>{category.channel?.name || '-'}</span>
      )
    },
    {
      accessor: 'isActive',
      label: 'Status',
      render: (isActive) => (
        <span className={`badge ${isActive ? 'bg-success' : 'bg-secondary'}`}>
          {isActive ? 'Ativo' : 'Inativo'}
        </span>
      )
    },
    {
      accessor: 'contentFetchedAt',
      label: 'Última Atualização',
      render: (date) => (
        <span>{date ? moment(String(date)).format('L LT') : '-'}</span>
      )
    },
    {
      accessor: 'actions',
      label: 'Ações',
      render: (_: any, category: AdminExternalNewsCategory) => (
        <div className="d-flex gap-2">
          <Button
            type="button"
            color="outline-secondary"
            size="sm"
            loading={isTriggeringOne}
            disabled={Boolean(category.isFetchingContent)}
            onClick={(e: React.MouseEvent) => handleTriggerOne(e, category.id)}
          >
            Atualizar
          </Button>
          <Icon icon="eye" className="text-primary" />
        </div>
      )
    }
  ];

  return (
    <div className="padding-page">
      <Row className="d-flex flex-column flex-md-row">
        <Col className="col-12 col-md">
          <div>
            <p className="text-gray-600">Administração</p>
            <h4 className="fw-bold">Categorias de Notícias</h4>
            <span className="small-typography text-gray-600">
              Gerencie as categorias de notícias externas da plataforma.
            </span>
          </div>
        </Col>
        <Col className="col-12 col-md align-text mt-3 mt-md-0 d-flex gap-2 justify-content-end">
          <Button
            type="button"
            color="secondary"
            loading={isTriggeringAll}
            onClick={handleTriggerAll}
          >
            Atualizar Todas
          </Button>
          <Link
            to="/admin/news/categories/create"
            className="btn btn-primary text-white button-register"
          >
            Cadastrar
          </Link>
        </Col>
      </Row>
      <Row className="d-flex align-items-center mt-2 flex-column flex-md-row">
        <Col className="col-12 col-md">
          <SearchInput
            onClearFilter={handleClearFilter}
            onSearchTextChange={handleSearch}
            placeholder="Buscar categoria"
          />
        </Col>
      </Row>
      <Table
        data={categoriesPage?.items || []}
        columns={columns}
        isLoading={isLoading}
        onRowClick={goToEdit}
      />
      <div className="d-flex justify-content-between align-items-center flex-column flex-md-row mt-3">
        {categoriesPage && (
          <PaginationNav
            onChange={handlePageChange}
            totalPages={categoriesPage.totalPages}
            currentPage={categoriesPage.page}
          />
        )}
      </div>
    </div>
  );
};

export default AdminNewsCategoriesListPage;
